/**
 * Image-model picker.
 *
 * Shared by the compact settings panel and the wizard's quality step so both
 * surfaces offer the same list, the same labels and the same tier hint. The
 * caller decides whether to render it at all (admins can turn user selection
 * off, and a single model is no choice).
 */

import { useTranslation } from "react-i18next"

import type { StudioImageModel } from "@/features/studio-ai/types/studio-ai.types"

import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { IMAGE_TIER_LABEL_KEYS } from "@/features/studio-ai/utils/image-model-capabilities"

type ImageModelSelectProps = {
  models: StudioImageModel[]
  value?: string
  disabled?: boolean
  /** DOM id of the trigger, also the label's target. */
  triggerId?: string
  onChange: (modelId: string) => void
}

export function ImageModelSelect({
  models,
  value,
  disabled,
  triggerId = "image-model-select",
  onChange,
}: ImageModelSelectProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.imageSettings" })

  // "Flare · Fast" — skip the family label when it just repeats the display
  // name, which is the common case for a single deployment per family.
  const hintFor = (model: StudioImageModel): string => {
    const tierKey = IMAGE_TIER_LABEL_KEYS[model.tier]
    const tierLabel = tierKey ? t(tierKey) : ""
    const familyLabel = model.family_label === model.display_name ? "" : model.family_label
    return [familyLabel, tierLabel].filter(Boolean).join(" · ")
  }

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium" htmlFor={triggerId}>
        {t("model", { defaultValue: "Modell" })}
      </Label>
      <Select disabled={disabled} value={value} onValueChange={onChange}>
        <SelectTrigger id={triggerId}>
          <SelectValue placeholder={t("selectModel", { defaultValue: "Välj modell" })} />
        </SelectTrigger>
        <SelectContent>
          {models.map((model) => {
            const hint = hintFor(model)
            return (
              <SelectItem key={model.id} value={model.id}>
                {model.display_name}
                {hint && <span className="text-muted-foreground ml-2 text-xs">{hint}</span>}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </div>
  )
}
